"use client"

import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import "swiper/css"
import { CustomImage, useIsDesktop } from "ui"
import { sharedColors } from "ui/theme/colors"
import { transitionEase, transitionTime } from "ui/transitions/utils"
import { HistoryData } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"

const StyledContainer = styled.div<{ $inView: boolean; $isReversed?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  position: relative;
  opacity: ${(props) => (props.$inView ? 1 : 0)};
  transition: all ${transitionTime}s ${transitionEase};
  flex-direction: column-reverse;
  gap: 24px;
  padding-inline-start: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-inline-start: 0;
    opacity: ${(props) => (props.$inView ? 1 : 0)};
    display: grid;
    grid-template-columns: 1fr 1fr;
    img {
      order: ${(props) => (props.$isReversed ? 1 : 0)};
      justify-self: ${(props) =>
        props.$isReversed ? "flex-start" : "flex-end"};
    }
    gap: 120px;
    flex-direction: ${(props) => (props.$isReversed ? "row-reverse" : "row")};
  }
`

const TextBox = styled.div<{ $isReversed: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;

  ${({ theme }) => theme.breakpoints.up("md")} {
    max-width: 50%;
    text-align: ${(p) => (p.$isReversed ? "right" : "left")};
    justify-self: ${(props) => (props.$isReversed ? "flex-end" : "flex-start")};
  }
`

const StyledImage = styled(CustomImage)`
  object-fit: cover;
  border-radius: 16px;
  height: 184px;
  width: 304px;

  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 487px;
    height: 260px;
    transform: translateY(-80px);
  }
`

const StyledPin = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 200px;
  z-index: 1;
  box-sizing: content-box;
  border: 3px solid var(--White, #fff);
  background: var(--Brand-Light, #1bbd77);
  box-shadow: 0px 0px 16px 0px rgba(27, 189, 119, 0.8);
  position: absolute;
  left: -7px;
  top: 0;
  ${({ theme }) => theme.breakpoints.up("md")} {
    left: 50%;
    transform: translateX(-50%); // Center the pin horizontally
  }
`

const StyledTypography = styled(Typography)<{ component?: React.ElementType }>`
  font-size: 24px !important;
  line-height: 24px !important;
  font-weight: 700 !important;
  margin: 0px;
  color: ${sharedColors.brandMain};
`

type Props = {
  el: HistoryData
  index: number
}

const TimelineItem: React.FC<Props> = ({ el, index }) => {
  const isDesktop = useIsDesktop()
  return (
    <StyledContainer
      $inView={true} // remove the inView animation for now
      $isReversed={index % 2 === 0}
    >
      <StyledImage
        src={getUmbracoMediaUrl(
          isDesktop ? el.imageForDesktop : el.imageForMobil,
        )}
        alt={el.title}
        width={487}
        height={260}
      />
      <StyledPin />
      <TextBox $isReversed={index % 2 === 0}>
        <StyledTypography component="h2" color={sharedColors.brandDark}>
          {el.years}
        </StyledTypography>
        <Typography variant="h3">{el.title}</Typography>
        <Typography variant="body1">{el.subtitle}</Typography>
      </TextBox>
    </StyledContainer>
  )
}

export default TimelineItem
